import { boundsOfShape } from './geometry';
import { regionRegistry } from './region-registry';
import type { BiomeId, Region } from './types';
import { WORLD_PIXEL_H, WORLD_PIXEL_W } from './types';

export function regionBounds(id: BiomeId): { x: number; y: number; w: number; h: number } {
  return { ...regionRegistry.get(id)!.bounds };
}

export function currentBiome(x: number, y: number): BiomeId {
  return regionRegistry.regionAtWorldPoint({ x, y }).biome;
}

export function regionObstacles(
  id: BiomeId,
): Array<{ x: number; y: number; w: number; h: number }> {
  return regionRegistry
    .get(id)!
    .collision.map((shape) => (shape.kind === 'rect' ? shape : boundsOfShape(shape)));
}

export function regionNpcSpots(id: BiomeId): Array<{ x: number; y: number }> {
  const bounds = regionBounds(id);
  return [
    {
      x: bounds.x + Math.floor(bounds.w * 0.25),
      y: bounds.y + Math.floor(bounds.h * 0.4),
    },
    {
      x: bounds.x + Math.floor(bounds.w * 0.6),
      y: bounds.y + Math.floor(bounds.h * 0.6),
    },
    {
      x: bounds.x + Math.floor(bounds.w * 0.4),
      y: bounds.y + Math.floor(bounds.h * 0.75),
    },
    {
      x: bounds.x + Math.floor(bounds.w * 0.8),
      y: bounds.y + Math.floor(bounds.h * 0.3),
    },
  ];
}

export function regionForBiome(id: BiomeId): Region {
  return {
    id,
    bounds: regionBounds(id),
    obstacles: regionObstacles(id),
    npcSpots: regionNpcSpots(id),
  };
}

export function allRegions(): Region[] {
  return regionRegistry.all().map((region) => regionForBiome(region.biome));
}

export function collidesWithObstacle(
  cx: number,
  cy: number,
  radius: number,
  obstacles: Array<{ x: number; y: number; w: number; h: number }>,
): boolean {
  for (const obstacle of obstacles) {
    const closestX = Math.max(obstacle.x, Math.min(cx, obstacle.x + obstacle.w));
    const closestY = Math.max(obstacle.y, Math.min(cy, obstacle.y + obstacle.h));
    const dx = cx - closestX;
    const dy = cy - closestY;
    if (dx * dx + dy * dy < radius * radius) return true;
  }
  return false;
}

export function clampToWorld(x: number, y: number, radius: number): { x: number; y: number } {
  return {
    x: Math.max(radius, Math.min(WORLD_PIXEL_W - radius, x)),
    y: Math.max(radius, Math.min(WORLD_PIXEL_H - radius, y)),
  };
}
